import { useState, useCallback } from 'react';
import { reportService } from '../services';
import { FeedbacksReportsResponse, FeedbacksReportsParams } from '../types/reports';
import { useAuth } from '../context/AuthContext';

export interface UseFeedbackReportsState {
  data: FeedbacksReportsResponse | null;
  loading: boolean;
  error: string | null;
}

export interface UseFeedbackReportsResult extends UseFeedbackReportsState {
  fetchReports: (params?: FeedbacksReportsParams) => Promise<void>;
  refetch: () => Promise<void>;
}

export const useFeedbackReports = (initialParams?: FeedbacksReportsParams): UseFeedbackReportsResult => {
  const { isAuthenticated, user } = useAuth();
  const [state, setState] = useState<UseFeedbackReportsState>({
    data: null,
    loading: false,
    error: null,
  });

  const [currentParams, setCurrentParams] = useState<FeedbacksReportsParams>(initialParams || {});

  const fetchReports = useCallback(async (params: FeedbacksReportsParams = {}) => {
    if (!isAuthenticated || !user?.roles?.includes('reportsReader')) {
      setState(prev => ({ 
        ...prev, 
        error: 'Access denied: reportsReader role required',
        loading: false 
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    setCurrentParams(params);

    try {
      const data = await reportService.getFeedbackReports(params);
      // console.log('Fetched feedback reports:', data);
      setState(prev => ({ ...prev, data, loading: false }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Failed to fetch feedback reports',
        loading: false 
      }));
    }
  }, [isAuthenticated, user?.roles]);

  const refetch = useCallback(() => {
    return fetchReports(currentParams);
  }, [fetchReports, currentParams]);

  // Note: Initial fetch is handled by parent Reports page via handleApplyFilter()
  // No auto-fetch on mount to avoid race conditions and inconsistent data

  return {
    ...state,
    fetchReports,
    refetch,
  };
};